import { useMemo, useState, type KeyboardEvent } from 'react';
import { z } from 'zod';
import { AlertTriangle, X } from 'lucide-react';
import { Card } from '@reflex/ui/components/ui/card';
import { Input } from '@reflex/ui/components/ui/input';
import { Button } from '@reflex/ui/components/ui/button';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@reflex/ui/components/ui/select';
import { Label } from '@reflex/ui/components/ui/label';
import { PanelTitle, PageDescription, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { EmailDomainSchema, EmailAddressSchema } from '@/lib/types';
import type { AuthAllowlistMode } from '@reflex/shared';

export interface DomainEditorProps {
  /**
   * Initial list of normalised domains. The component manages a local copy
   * for editing; pass a fresh value via React `key` to remount when the
   * server returns a new canonical list.
   */
  initial: string[];
  /**
   * Initial list of normalised individual email addresses. Defaults to
   * `[]` so callers that pre-date the per-email allowlist keep working.
   */
  initialEmails?: string[];
  /**
   * Initial allowlist mode. Defaults to `'allow'` for backward compat with
   * legacy callers that haven't started passing a mode yet.
   */
  initialMode?: AuthAllowlistMode;
  /**
   * Persists the domain list, per-email list, and the chosen mode. The
   * `emails` argument is third so older two-arg callers that only
   * accept `(domains, mode)` still type-check at the call site through
   * a default — when in doubt, accept all three.
   */
  onSave: (domains: string[], mode: AuthAllowlistMode, emails: string[]) => Promise<void>;
  isSaving: boolean;
  /**
   * Optional title rendered in the card header. Defaults to "Allowed email
   * domains". Pass `null` to suppress the header entirely (useful when the
   * surrounding layout already provides a heading).
   */
  title?: string | null;
  /**
   * Optional description rendered under the title. Defaults to the standard
   * helper text. Pass `null` to suppress.
   */
  description?: React.ReactNode | null;
  /**
   * Optional ID/label for the user's own email domain. When provided, the
   * editor surfaces a one-click "Add my domain" suggestion — useful in the
   * setup wizard so the admin doesn't accidentally lock themselves out.
   */
  suggestion?: { domain: string; label: string };
  /**
   * Hide the inner Card chrome — useful when the parent already renders one.
   */
  bare?: boolean;
}

const DEFAULT_DESCRIPTION = (
  <>
    Choose how email domains are checked at sign-in. Use the list to allow only specific domains, or
    to deny specific ones. Do not include the leading <code>@</code>.
  </>
);

const MODE_LABELS: Record<AuthAllowlistMode, { label: string; help: string }> = {
  off: {
    label: 'Off: every domain may sign in',
    help: 'The list is ignored. Use this when org-level email domain claims are sufficient.',
  },
  allow: {
    label: 'Allow only listed domains',
    help: 'Only users whose email domain matches one of these may sign in. An empty list blocks everyone.',
  },
  deny: {
    label: 'Deny listed domains',
    help: 'Every domain may sign in except those in the list.',
  },
};

/**
 * Reusable chip-based editor for the cross-provider email-domain allowlist.
 * Used both in the first-run setup wizard and on the admin auth settings
 * page. The component is "uncontrolled" with respect to the server: callers
 * pass `initial` and an `onSave` mutator, and re-mount the editor (via a
 * `key` derived from the latest server canonical list) whenever they want
 * to reset the local state.
 */
export function DomainEditor({
  initial,
  initialEmails = [],
  initialMode = 'allow',
  onSave,
  isSaving,
  title = 'Email domain policy',
  description = DEFAULT_DESCRIPTION,
  suggestion,
  bare = false,
}: DomainEditorProps) {
  const [domains, setDomains] = useState<string[]>(initial);
  const [emails, setEmails] = useState<string[]>(initialEmails);
  const [mode, setMode] = useState<AuthAllowlistMode>(initialMode);
  const [domainDraft, setDomainDraft] = useState('');
  const [domainDraftError, setDomainDraftError] = useState<string | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailDraftError, setEmailDraftError] = useState<string | null>(null);

  const dirty = useMemo(() => {
    if (mode !== initialMode) return true;
    if (domains.length !== initial.length) return true;
    for (let i = 0; i < domains.length; i++) {
      if (domains[i] !== initial[i]) return true;
    }
    if (emails.length !== initialEmails.length) return true;
    for (let i = 0; i < emails.length; i++) {
      if (emails[i] !== initialEmails[i]) return true;
    }
    return false;
  }, [domains, initial, emails, initialEmails, mode, initialMode]);

  const suggestionDomain = suggestion?.domain.toLowerCase();
  const showSuggestion =
    !!suggestionDomain &&
    z.validate(EmailDomainSchema, suggestionDomain) &&
    !domains.includes(suggestionDomain);

  function tryAddDomain(value?: string) {
    const candidate = (value ?? domainDraft).trim();
    if (candidate.length === 0) return;
    const parsed = EmailDomainSchema.safeParse(candidate);
    if (!parsed.success) {
      setDomainDraftError(parsed.error.issues[0]?.message ?? 'Invalid domain');
      return;
    }
    const next = parsed.data;
    if (domains.includes(next)) {
      setDomainDraftError(`"${next}" is already in the list`);
      return;
    }
    setDomains([...domains, next]);
    if (value === undefined) setDomainDraft('');
    setDomainDraftError(null);
  }

  function tryAddEmail(value?: string) {
    const candidate = (value ?? emailDraft).trim();
    if (candidate.length === 0) return;
    const parsed = EmailAddressSchema.safeParse(candidate);
    if (!parsed.success) {
      setEmailDraftError(parsed.error.issues[0]?.message ?? 'Invalid email');
      return;
    }
    const next = parsed.data;
    if (emails.includes(next)) {
      setEmailDraftError(`"${next}" is already in the list`);
      return;
    }
    setEmails([...emails, next]);
    if (value === undefined) setEmailDraft('');
    setEmailDraftError(null);
  }

  function handleDomainKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAddDomain();
    } else if (e.key === 'Backspace' && domainDraft.length === 0 && domains.length > 0) {
      // Quick-delete the last chip, mirroring most tag-input UIs.
      e.preventDefault();
      setDomains(domains.slice(0, -1));
    }
  }

  function handleEmailKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      tryAddEmail();
    } else if (e.key === 'Backspace' && emailDraft.length === 0 && emails.length > 0) {
      e.preventDefault();
      setEmails(emails.slice(0, -1));
    }
  }

  function handleRemoveDomain(domain: string) {
    setDomains(domains.filter((d) => d !== domain));
  }

  function handleRemoveEmail(email: string) {
    setEmails(emails.filter((e) => e !== email));
  }

  async function handleSave() {
    try {
      await onSave(domains, mode, emails);
    } catch (err) {
      // The mutation hook surfaces an error toast; we just need to keep the
      // local state intact so the user can retry.
      void err;
    }
  }

  function handleReset() {
    setDomains(initial);
    setEmails(initialEmails);
    setMode(initialMode);
    setDomainDraft('');
    setDomainDraftError(null);
    setEmailDraft('');
    setEmailDraftError(null);
  }

  const Wrapper = bare ? 'div' : Card;
  const wrapperClass = bare ? 'space-y-4' : 'p-4 space-y-4';

  return (
    <Wrapper className={wrapperClass}>
      {title !== null && (
        <div>
          <PanelTitle as="h2" size="sm">
            {title}
          </PanelTitle>
          {description != null && (
            <PageDescription className="mt-0.5 text-xs">{description}</PageDescription>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="auth-domain-mode">Mode</Label>
        <Select
          value={mode}
          onValueChange={(value) => setMode(value as AuthAllowlistMode)}
          disabled={isSaving}
        >
          <SelectTrigger id="auth-domain-mode" data-testid="auth-domain-mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="off">{MODE_LABELS.off.label}</SelectItem>
            <SelectItem value="allow">{MODE_LABELS.allow.label}</SelectItem>
            <SelectItem value="deny">{MODE_LABELS.deny.label}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{MODE_LABELS[mode].help}</p>
      </div>

      {mode !== 'off' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <SectionLabel as="span">Domains</SectionLabel>
            {domains.length === 0 ? (
              <Alert variant="default">
                <AlertTriangle className="size-4" />
                <AlertTitle>No domains configured</AlertTitle>
                <AlertDescription>
                  {mode === 'allow'
                    ? 'No domains are allowed. Add a domain or add specific emails below.'
                    : 'No domains are denied. Add a domain or add specific emails below.'}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="auth-domain-chips">
                {domains.map((domain) => (
                  <Badge
                    key={domain}
                    variant="outline"
                    className="gap-1.5 bg-muted/40 py-1 pl-2.5 pr-1 leading-none"
                    data-testid={`auth-domain-chip-${domain}`}
                  >
                    <span className="font-mono">{domain}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => handleRemoveDomain(domain)}
                      aria-label={`Remove ${domain}`}
                      className="rounded-full -mr-0.5"
                      disabled={isSaving}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="auth-domain-input" className="sr-only">
                Add domain
              </Label>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Input
                    id="auth-domain-input"
                    value={domainDraft}
                    onChange={(e) => {
                      setDomainDraft(e.target.value);
                      if (domainDraftError) setDomainDraftError(null);
                    }}
                    onKeyDown={handleDomainKeyDown}
                    placeholder="e.g. runloop.ai"
                    aria-label="Add domain"
                    aria-invalid={domainDraftError ? true : undefined}
                    disabled={isSaving}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {domainDraftError && (
                    <p role="alert" className="mt-1 text-xs text-destructive">
                      {domainDraftError}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => tryAddDomain()}
                  disabled={domainDraft.trim().length === 0 || isSaving}
                >
                  Add
                </Button>
              </div>
              {showSuggestion && (
                <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                  <span>{suggestion!.label}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => tryAddDomain(suggestionDomain)}
                    disabled={isSaving}
                    data-testid="auth-domain-suggestion"
                  >
                    Add <code className="ml-1 font-mono">{suggestionDomain}</code>
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <SectionLabel as="span">Emails</SectionLabel>
            <p className="text-xs text-muted-foreground">
              {mode === 'allow'
                ? 'Individual email addresses that can sign in regardless of domain. Useful for inviting one-off users (contractors, vendors).'
                : 'Individual email addresses that are blocked regardless of domain.'}
            </p>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="auth-email-chips">
                {emails.map((email) => (
                  <Badge
                    key={email}
                    variant="outline"
                    className="gap-1.5 bg-muted/40 py-1 pl-2.5 pr-1 leading-none"
                    data-testid={`auth-email-chip-${email}`}
                  >
                    <span className="font-mono">{email}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => handleRemoveEmail(email)}
                      aria-label={`Remove ${email}`}
                      className="rounded-full -mr-0.5"
                      disabled={isSaving}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="auth-email-input" className="sr-only">
                Add email
              </Label>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <Input
                    id="auth-email-input"
                    value={emailDraft}
                    onChange={(e) => {
                      setEmailDraft(e.target.value);
                      if (emailDraftError) setEmailDraftError(null);
                    }}
                    onKeyDown={handleEmailKeyDown}
                    placeholder="e.g. alex@example.com"
                    aria-label="Add email"
                    aria-invalid={emailDraftError ? true : undefined}
                    disabled={isSaving}
                    autoComplete="off"
                    spellCheck={false}
                    type="email"
                  />
                  {emailDraftError && (
                    <p role="alert" className="mt-1 text-xs text-destructive">
                      {emailDraftError}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => tryAddEmail()}
                  disabled={emailDraft.trim().length === 0 || isSaving}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t pt-3">
        <Button type="button" variant="ghost" onClick={handleReset} disabled={!dirty || isSaving}>
          Reset
        </Button>
        <Button type="button" onClick={handleSave} disabled={!dirty || isSaving}>
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </Wrapper>
  );
}
